import { useEffect, useRef, useState } from 'react';
import { animate, useReducedMotion } from 'framer-motion';
import { formatSurplusCurrency, formatSurplusUnitPrice, getSurplusQuote, isValidSurplusQuantity } from '@/utils/surplusPricing';

function UnitPriceCounter({ value }: { value: number }) {
  const [displayed, setDisplayed] = useState(value);
  const current = useRef(value);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (reduceMotion) { current.current = value; setDisplayed(value); return; }
    const controls = animate(current.current, value, { duration: 0.32, ease: 'easeOut', onUpdate: number => {
      current.current = number; setDisplayed(number);
    } });
    return () => controls.stop();
  }, [value, reduceMotion]);
  return <p aria-label="Preço por play" aria-live="polite" className="text-[14px] font-semibold tabular-nums text-[#2563EB]">
    <span className="sr-only">1 Play = R$ {formatSurplusUnitPrice(value)}</span>
    <span aria-hidden="true">1 Play = R$ {formatSurplusUnitPrice(displayed)}</span>
  </p>;
}

interface Props {
  quantity: number;
  lockedPrice?: { total_cents?: number; unit_amount_decimal?: string } | null;
}

export function SurplusPricingSummary({ quantity, lockedPrice }: Props) {
  const valid = isValidSurplusQuantity(quantity);
  const quote = valid ? getSurplusQuote(quantity) : null;
  // Tentativas antigas sem snapshot foram iniciadas no preço anterior de R$0,02.
  const unitPrice = lockedPrice ? Number(lockedPrice.unit_amount_decimal || '2') / 100 : quote?.unitPrice;
  const total = lockedPrice ? (lockedPrice.total_cents ?? quantity * 2) : quote?.total_cents;
  const tiers = [
    { label: 'De 1 mil a 99.999', price: 0.02 },
    { label: 'A partir de 100 mil', price: 0.014 },
    { label: 'A partir de 1 milhão', price: 0.01 },
  ];
  return <div className="mb-5 mt-3 text-center">
    {unitPrice !== undefined && valid ? <UnitPriceCounter value={unitPrice} /> : <p className="text-sm text-[#6B7280]">Informe a quantidade para calcular o preço</p>}
    <p className="mt-1 text-[18px] font-bold tabular-nums text-[#111827]">
      Total: {total !== undefined && valid ? `R$ ${formatSurplusCurrency(total / 100)}` : '—'}
    </p>
    <ul aria-label="Preços por quantidade" className="mt-3 space-y-1 text-xs text-[#6B7280]">
      {tiers.map(tier => <li key={tier.label} className={!lockedPrice && unitPrice === tier.price ? 'font-semibold text-[#2563EB]' : ''}>
        {tier.label}: R$ {formatSurplusUnitPrice(tier.price)} por play
      </li>)}
    </ul>
    <p className="mt-2 text-[11px] text-[#6B7280]">{lockedPrice ? 'Valor original da compra em andamento.' : 'O preço da faixa vale para todos os plays desta compra.'}</p>
  </div>;
}
